import React from 'react';
import { Link } from 'react-router-dom';
import { LuCheck, LuArrowRight } from 'react-icons/lu';
import useInView from '../hooks/useInView';

// Tier feature differentiation. Currently DISPLAY-ONLY — every account
// gets every feature regardless of plan. We're testing whether the staged
// capability story moves conversion before committing to enforcement
// plumbing. If it does, the gates land in a follow-up PR.
const PLANS = [
  {
    name: 'Starter',
    monthly: 25,
    tagline: 'Solo operators & side projects',
    highlights: [
      '3 SIM cards',
      'Unlimited messages',
      'Unlimited GoHighLevel sub-accounts',
      'REST API + webhooks',
      'Web dashboard',
    ],
  },
  {
    name: 'Agency',
    monthly: 65,
    tagline: 'Agencies & small sales teams',
    highlights: [
      '10 SIM cards',
      'Unlimited messages',
      'Unlimited GoHighLevel sub-accounts',
      'REST API + webhooks',
      'Team logins (subusers)',
    ],
    popular: true,
  },
  {
    name: 'Scale',
    monthly: 195,
    tagline: 'Scale without ceiling',
    highlights: [
      'Unlimited SIM cards',
      'Unlimited GoHighLevel sub-accounts',
      'Dedicated server for your devices',
      'Everything in Agency',
      'Priority support (Slack)',
      'Custom invoicing & SLA',
    ],
  },
];

// Twilio avg at $0.0079/SMS for outbound US messages.
const TWILIO_PER_SMS_CENTS = 0.79;

export default function HomePricing() {
  const [ref, inView] = useInView();

  return (
    <section ref={ref} className={`hp${inView ? ' in-view' : ''}`}>
      <div className="hp-container">
        <header className="hp-head">
          <span className="hp-eyebrow">PRICING</span>
          <h2 className="hp-title">Your whole team, for less than one Twilio retainer.</h2>
          <p className="hp-sub">
            From <strong>$0.001/SMS</strong> through your own SIM. Compare to Twilio at ~$0.0079/SMS and the math gets uncomfortable fast.
          </p>
        </header>

        <div className="hp-grid">
          {PLANS.map((p, i) => (
            <article key={p.name} className={`hp-card${p.popular ? ' hp-card--popular' : ''}`} style={{ '--i': i }}>
              {p.popular && <div className="hp-popular">Most popular</div>}
              <div className="hp-name">{p.name}</div>
              <div className="hp-price">
                <span className="hp-price-currency">$</span>
                <span className="hp-price-amount">{p.monthly}</span>
                <span className="hp-price-per">/mo</span>
              </div>
              <div className="hp-tagline">{p.tagline}</div>
              <ul className="hp-list">
                {p.highlights.map((h) => (
                  <li key={h}>
                    <LuCheck size={13} strokeWidth={2.5} />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" className={`hp-cta${p.popular ? ' hp-cta--dark' : ''}`}>
                Start free <LuArrowRight size={14} strokeWidth={2} />
              </Link>
            </article>
          ))}
        </div>

        <div className="hp-foot">
          <Link to="/pricing" className="hp-foot-link">
            Full pricing & FAQ <LuArrowRight size={13} strokeWidth={2} />
          </Link>
          <span className="hp-foot-sep">·</span>
          <span className="hp-foot-note">
            All plans include unlimited messages. Save 17% on annual.
          </span>
        </div>
      </div>

      <style jsx>{`
        .hp {
          background: #ffffff;
          padding: clamp(5rem, 12vh, 9rem) clamp(1rem, 3vw, 2rem);
          font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #0a0a0a;
          -webkit-font-smoothing: antialiased;
          letter-spacing: -0.005em;
          border-top: 1px solid rgba(0,0,0,0.05);
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }
        .hp-container { max-width: 1200px; margin: 0 auto; }

        .hp-head { text-align: center; margin-bottom: clamp(3rem, 6vh, 4.5rem); }
        .hp-eyebrow {
          display: inline-block;
          font-size: 0.6875rem; font-weight: 600;
          letter-spacing: 0.14em; text-transform: uppercase;
          color: rgba(0,0,0,0.5);
          margin-bottom: 1.25rem;
        }
        .hp-title {
          font-size: clamp(2rem, 4.8vw, 3.25rem);
          font-weight: 500;
          letter-spacing: -0.035em;
          line-height: 1.05;
          margin: 0 auto 1rem;
          max-width: 20ch;
        }
        .hp-sub {
          font-size: clamp(0.9375rem, 1.4vw, 1.0625rem);
          color: rgba(0,0,0,0.55);
          line-height: 1.55;
          margin: 0 auto;
          max-width: 540px;
        }
        .hp-sub strong { color: #0a0a0a; font-weight: 600; }

        .hp-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 1rem;
          max-width: 1000px;
          margin: 0 auto;
        }
        @media (max-width: 900px) { .hp-grid { grid-template-columns: 1fr; max-width: 420px; } }

        .hp-head, .hp-card, .hp-foot {
          opacity: 0;
          translate: 0 14px;
          transition: opacity 620ms cubic-bezier(0.16, 1, 0.3, 1), translate 620ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hp.in-view .hp-head { opacity: 1; translate: 0 0; }
        .hp.in-view .hp-card { opacity: 1; translate: 0 0; transition-delay: calc(120ms + var(--i, 0) * 100ms); }
        .hp.in-view .hp-foot { opacity: 1; translate: 0 0; transition-delay: 500ms; }

        .hp-card {
          position: relative;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.08);
          border-radius: 16px;
          padding: clamp(1.5rem, 2.5vw, 2rem);
          display: flex;
          flex-direction: column;
          gap: 0.875rem;
          transition: border-color 220ms ease, transform 220ms ease, box-shadow 220ms ease, background 220ms ease, opacity 620ms cubic-bezier(0.16, 1, 0.3, 1), translate 620ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .hp-card:hover {
          transform: translateY(-3px);
          border-color: rgba(0,0,0,0.18);
          box-shadow: 0 12px 28px -14px rgba(0,0,0,0.1);
        }
        .hp-card--popular {
          background: linear-gradient(180deg, #faf7ff 0%, #ffffff 70%);
          border-color: rgba(124, 58, 237, 0.35);
          box-shadow: 0 20px 40px -18px rgba(124, 58, 237, 0.2);
        }
        .hp-card--popular:hover {
          border-color: rgba(124, 58, 237, 0.5);
          box-shadow: 0 24px 48px -18px rgba(124, 58, 237, 0.28);
        }
        .hp-popular {
          position: absolute;
          top: -11px; left: 50%; transform: translateX(-50%);
          background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
          color: #fff;
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 0.3rem 0.875rem;
          border-radius: 999px;
          box-shadow: 0 6px 16px -4px rgba(124, 58, 237, 0.5);
          white-space: nowrap;
        }

        .hp-name {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #0a0a0a;
          letter-spacing: -0.01em;
        }
        .hp-price {
          display: flex;
          align-items: baseline;
          gap: 0.15rem;
          margin-top: 0.25rem;
        }
        .hp-price-currency {
          font-size: 1.25rem;
          font-weight: 600;
          color: #0a0a0a;
        }
        .hp-price-amount {
          font-size: clamp(2.5rem, 5vw, 3rem);
          font-weight: 600;
          letter-spacing: -0.045em;
          line-height: 1;
          color: #0a0a0a;
        }
        .hp-price-per {
          font-size: 0.9375rem;
          color: rgba(0,0,0,0.55);
          margin-left: 0.15rem;
        }
        .hp-tagline {
          font-size: 0.8125rem;
          color: rgba(0,0,0,0.52);
          padding-bottom: 0.875rem;
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }
        .hp-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          flex: 1;
        }
        .hp-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #0a0a0a;
          line-height: 1.4;
        }
        .hp-list li :global(svg) {
          color: #6d28d9;
          flex-shrink: 0;
          margin-top: 3px;
        }

        .hp-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          margin-top: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          transition: background 160ms ease, transform 120ms ease, color 160ms ease;
          background: transparent;
          color: #0a0a0a;
          border: 1px solid rgba(0,0,0,0.14);
        }
        .hp-cta:hover {
          background: #fafafa;
          border-color: rgba(0,0,0,0.28);
          color: #0a0a0a;
          transform: translateY(-1px);
        }
        .hp-cta--dark {
          background: #0a0a0a;
          color: #fff;
          border-color: #0a0a0a;
        }
        .hp-cta--dark:hover {
          background: #27272a;
          color: #fff;
        }

        .hp-foot {
          margin-top: clamp(2rem, 4vh, 3rem);
          text-align: center;
          font-size: 0.875rem;
          color: rgba(0,0,0,0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .hp-foot-link {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          color: #0a0a0a;
          text-decoration: none;
          font-weight: 500;
        }
        .hp-foot-link:hover { text-decoration: underline; text-underline-offset: 2px; color: #0a0a0a; }
        .hp-foot-sep { color: rgba(0,0,0,0.25); }
      `}</style>
    </section>
  );
}
